import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Briefcase, Users, TrendingUp, Award, Heart, Zap, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { SEOHead } from "@/components/seo";
import { generateOrganizationSchema } from "@/components/seo/SchemaMarkup";
import { trackPixelLead } from "@/lib/retargeting";
import { trackGA4Event } from "@/lib/ga4";

const careerApplicationSchema = z.object({
  fullName: z.string().min(1, "Full name is required").max(100),
  email: z.string().email("Invalid email address").max(255),
  phone: z.string().max(30).optional(),
  licenseNumber: z.string().max(50).optional(),
  message: z.string().max(2000).optional(),
  tcpaConsent: z.boolean().optional(),
});

type CareerApplicationForm = z.infer<typeof careerApplicationSchema>;

const Careers = () => {
  useEffect(() => {
    document.title = "Careers | Prime Realty Partners";
  }, []);

  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<CareerApplicationForm>>({
    fullName: "",
    email: "",
    phone: "",
    licenseNumber: "",
    message: "",
    tcpaConsent: false as unknown as true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const benefits = [
    { icon: TrendingUp, title: "Competitive Splits", desc: "Industry-leading commission structures" },
    { icon: Users, title: "Training & Mentorship", desc: "Ongoing education and support" },
    { icon: Zap, title: "Cutting-Edge Tech", desc: "Best-in-class tools and platforms" },
    { icon: Award, title: "Investor Friendly Brokerage", desc: "Dedicated to investor success" },
    { icon: Heart, title: "Supportive Culture", desc: "Collaborative team environment" },
    { icon: Briefcase, title: "Lead Generation", desc: "Company-provided leads" },
  ];

  const handleInputChange = (field: keyof CareerApplicationForm, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const result = careerApplicationSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert into career_applications table
      const { error: dbError } = await supabase
        .from("career_applications")
        .insert({
          full_name: result.data.fullName,
          email: result.data.email,
          phone: result.data.phone || "",
          license_number: result.data.licenseNumber || null,
          message: result.data.message || null,
        });

      if (dbError) throw dbError;

      // Sync to Follow Up Boss
      try {
        await supabase.functions.invoke("submit-lead", {
          body: {
            event_type: "agent_inquiry",
            lead_data: {
              name: result.data.fullName,
              email: result.data.email,
              phone: result.data.phone || "",
              message: result.data.licenseNumber 
                ? `License: ${result.data.licenseNumber}\n\n${result.data.message || ""}`
                : result.data.message || "",
              tcpa_consent: result.data.tcpaConsent || false,
            },
          },
        });
      } catch (fubError) {
        // Log but don't fail the submission if FUB sync fails
        console.error("FUB sync error:", fubError);
      }

      toast({
        title: "Application Submitted!",
        description: "Thank you for your interest. We'll be in touch soon.",
      });

      // Track Lead conversion for retargeting pixels
      trackPixelLead({
        formType: "career_application",
        contentName: "Careers Page - Agent Application",
      });

      trackGA4Event('form_submit', { event_category: 'lead_action', form_name: 'career_application', form_page: window.location.pathname });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        licenseNumber: "",
        message: "",
        tcpaConsent: false as unknown as true,
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const organizationSchema = generateOrganizationSchema();

  const careersWebPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Real Estate Careers at Prime Realty Partners",
    description: "Join Prime Realty Partners — South Jersey's growing real estate brokerage. Competitive splits, investor-friendly culture, and the tools you need to succeed.",
    url: "https://www.prpsj.com/careers",
    publisher: {
      "@type": "RealEstateBrokerage",
      name: "Prime Realty Partners",
      url: "https://www.prpsj.com",
    },
  };

  return (
    <Layout>
      <SEOHead
        title="Careers | Prime Realty Partners"
        description="Join Prime Realty Partners. We're hiring licensed real estate agents in South Jersey. See open positions and what makes our brokerage different."
        canonicalUrl="https://www.prpsj.com/careers"
        keywords="real estate careers South Jersey, become a realtor NJ, real estate agent jobs, Prime Realty Partners careers"
        schema={[organizationSchema, careersWebPageSchema]}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Careers", url: "https://www.prpsj.com/careers" }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-background py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Build your career with Prime Realty Partners. We're looking for passionate 
              individuals who want to excel in real estate.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="pt-8 pb-16">
        <div className="container">
          <h2 className="font-heading text-3xl font-bold mb-4 text-center">
            Why Join Prime Realty Partners?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We provide the tools, training, and support you need to succeed in today's 
            competitive real estate market.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <benefit.icon className="h-10 w-10 text-primary mb-4" />
                  <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-16 bg-muted/30">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl font-bold mb-4">
                Ready To Jump Start Your Real Estate Career?
              </h2>
              <p className="text-muted-foreground">
                Ready to take the next step in your real estate career? Fill out the form below 
                and we'll be in touch.
              </p>
            </div>

            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      placeholder="John Smith"
                      value={formData.fullName || ""}
                      onChange={(e) => handleInputChange("fullName", e.target.value)}
                      className={errors.fullName ? "border-destructive" : ""}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">{errors.fullName}</p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email || ""}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone || ""}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">Real Estate License Number (Optional)</Label>
                    <Input
                      id="licenseNumber"
                      placeholder="e.g., 123456789"
                      value={formData.licenseNumber || ""}
                      onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Tell Us About Yourself (Optional)</Label>
                    <Textarea
                      id="message"
                      placeholder="Share your background, experience, and what you're looking for..."
                      rows={4}
                      value={formData.message || ""}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                    />
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="tcpaConsent"
                      checked={formData.tcpaConsent === true}
                      onCheckedChange={(checked) => handleInputChange("tcpaConsent", checked === true)}
                      className={errors.tcpaConsent ? "border-destructive" : ""}
                    />
                    <div className="space-y-1">
                      <label
                        htmlFor="tcpaConsent"
                        className="text-sm leading-tight cursor-pointer"
                      >
                        I agree to be contacted by Prime Realty Partners via phone, email, or text 
                        regarding career opportunities.
                      </label>
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full gap-2" disabled={isSubmitting}>
                    {isSubmitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Submit Application
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    By submitting this form, you consent to receive communications from 
                    Prime Realty Partners. Your information will be handled in accordance 
                    with our privacy policy.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Careers;
